import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import AdminShell from "./AdminShell";
import { firestore } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ---------- UI (nya designen) ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const Muted = styled.div`
  margin-top: 8px;
  color: #6b7280;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.5;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const PreviewBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
  background: #fafafa;
`;

const PreviewHtml = styled.div`
  color: #111827;
  font-weight: 700;
  line-height: 1.65;

  p {
    margin: 0 0 10px;
  }
  a {
    color: #0f766e;
    font-weight: 900;
  }
  h1, h2, h3 {
    margin: 10px 0 10px;
  }
  ul, ol {
    margin: 8px 0 8px 18px;
  }
`;

const QuillWrap = styled.div`
  margin-top: 12px;

  .ql-toolbar.ql-snow {
    border-radius: 16px 16px 0 0;
    border-color: #e5e7eb;
    background: #fff;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 16px 16px;
    border-color: #e5e7eb;
    min-height: 260px;
    background: #fff;
  }

  .ql-editor {
    font-weight: 700;
    color: #111827;
    line-height: 1.65;
  }
`;

/* ---------- Component ---------- */

export default function AdminRules() {
  const DOC_REF = doc(firestore, "rules", "general");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [rules, setRules] = useState("");
  const [editorValue, setEditorValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchRules = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(DOC_REF);
        if (snap.exists()) {
          const value = snap.data()?.rules || "";
          setRules(value);
          setEditorValue(value);
        } else {
          setRules("");
          setEditorValue("");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = () => {
    setEditorValue(rules || "");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setEditorValue(rules || "");
    setIsEditing(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await setDoc(
        DOC_REF,
        { rules: editorValue, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setRules(editorValue);
      setIsEditing(false);
      alert("Sparat!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara regler.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Regler">
      <Grid>
        <Card>
          <Title>Regler (redigera)</Title>
          <Muted>
            Sparas i Firestore: <code>rules/general</code> → fält <b>rules</b>.
          </Muted>

          {loading ? (
            <Muted style={{ marginTop: 12 }}>Laddar…</Muted>
          ) : (
            <>
              {isEditing ? (
                <QuillWrap>
                  <ReactQuill
                    value={editorValue}
                    onChange={setEditorValue}
                    theme="snow"
                    modules={{
                      toolbar: [
                        [{ header: "1" }, { header: "2" }, { font: [] }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["bold", "italic", "underline"],
                        ["link"],
                        ["blockquote"],
                        [{ align: [] }],
                        ["image"],
                        ["clean"],
                      ],
                    }}
                  />
                </QuillWrap>
              ) : (
                <PreviewBox>
                  {rules ? (
                    <PreviewHtml dangerouslySetInnerHTML={{ __html: rules }} />
                  ) : (
                    <Muted>Inga regler sparade ännu.</Muted>
                  )}
                </PreviewBox>
              )}

              <BtnRow>
                {isEditing ? (
                  <>
                    <Btn type="button" onClick={save} disabled={busy}>
                      {busy ? "Sparar…" : "Spara ändringar"}
                    </Btn>
                    <Ghost type="button" onClick={cancelEdit} disabled={busy}>
                      Avbryt
                    </Ghost>
                  </>
                ) : (
                  <Btn type="button" onClick={startEdit} disabled={loading}>
                    Redigera
                  </Btn>
                )}
              </BtnRow>
            </>
          )}
        </Card>

        <Card>
          <Title>Info</Title>
          <Muted>
            Vill du att “Regler” även ska ha en bild (som News med <b>image1</b>) så kan vi lägga till upload till
            Storage och spara URL i dokumentet.
          </Muted>
        </Card>
      </Grid>
    </AdminShell>
  );
}





